import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { Injectable, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ActivatedRouteSnapshot, Resolve, RouterModule, RouterStateSnapshot, Routes } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import 'hammerjs';

import { FuseProgressBarModule, FuseSidebarModule, FuseThemeOptionsModule } from '@fuse/components';
import { FuseModule } from '@fuse/fuse.module';
import { FuseSharedModule } from '@fuse/shared.module';

import { fuseConfig } from 'app/fuse-config';

import { registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';

import { MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserTransferStateModule } from '@angular/platform-browser';
import { ServiceLocator } from '@shared/service.locator';
import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { TokenInterceptor } from 'app/modules-services/auth-services/token-interceptor';
import { Observable } from 'rxjs';
import { BadgeService } from './shared/badge.service';
import { AuthGuard } from './modules-services/auth-services/auth.guard';
import { CacheInterceptor } from './modules-services/auth-services/cache-interceptor';
import { JsonDateParser, JsonParser, JsonParserHttpInterceptor } from './modules-services/auth-services/json-parser.interceptor';
import { NavigationGuard } from './navigation/guards/navigation.guard';
import { ConfermaDialogModule } from './shared/conferma-dialog/conferma-dialog.module';
import { DirectivesModule } from './shared/directives/directives.module';
import { GlobalLoaderModule } from './shared/global-loader-folder/global-loader.module';
import { SharedModule } from './shared/shared.module';

@Injectable({ providedIn: 'root' })
class InitialDataResolver implements Resolve<any> {
    constructor(private badgeService: BadgeService) {}

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
        // Unica chiamata API che popola tutti i badge di navigazione
        return this.badgeService.loadAllBadges();
    }
}

const routes: Routes = [
    {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
    },
    {
        path: 'auth',
        loadChildren: () => import('./authentication/authentication.module').then(mod => mod.AuthenticationModule),
    },
    {
        path: '',
        resolve: { initialData: InitialDataResolver },
        canActivate: [AuthGuard, NavigationGuard],
        children: [
            {
                path: 'gestione-tabelle',
                loadChildren: () => import('./main/gestione-tabelle/gestione-tabelle.module').then(m => m.GestioneTabelleModule),
            },
            {
                path: 'forfettario',
                loadChildren: () => import('./main/forfettario-folder/forfettario.module').then(mod => mod.ForfettarioModule),
            },
            {
                path: 'registrazioni',
                loadChildren: () =>
                    import('./main/forfettari-in-registrazione-folder/forfettari-in-registrazione.module').then(mod => mod.ForfettarioInRegistrazioneModule),
            },
            {
                path: 'cassetti-fiscali-da-controllare',
                loadChildren: () =>
                    import('./main/cassetti-fiscali-da-controllare/cassetti-fiscali-da-controllare.module').then(mod => mod.CassettiFiscaliDaControllareModule),
            },
            {
                path: 'gestione-inps',
                loadChildren: () => import('./main/inps/inps-module').then(mod => mod.GestioneInpsModule),
            },
            {
                path: 'ruoli-operatori',
                loadChildren: () => import('./main/ruoli-operatori-folder/ruoli-operatori.module').then(mod => mod.RuoliOperatoriModule),
            },
            {
                path: 'emissioni',
                loadChildren: () => import('./main/emissioni-folder/emissioni.module').then(mod => mod.EmissioniModule),
            },
            {
                path: 'dashboard',
                loadChildren: () => import('./main/dashboard/dashboard.module').then(mod => mod.DashboardModule),
            },
            {
                path: 'cassetto-fiscale',
                loadChildren: () => import('./main/cassetto-fiscale-folder/cassetto-fiscale.module').then(mod => mod.CassettoFiscaleModule),
            },
            {
                path: 'importazioni-storico-da-enti',
                loadChildren: () =>
                    import('./main/importazioni-storico-da-enti/importazioni-storico-da-enti.module').then(m => m.ImportazioniStoricoDaEntiModule),
            },
            {
                path: 'auto-liquidazione-inail',
                loadChildren: () => import('./main/inail/auto-liquidazione.module').then(mod => mod.AutoLiquidazioneModule),
            },
            {
                path: 'criticita',
                loadChildren: () => import('./main/criticita/criticita.module').then(m => m.CriticitaModule),
            },
            {
                path: 'gestione-scadenze',
                loadChildren: () => import('./main/gestione-scadenze-folder/gestione-scadenze.module').then(mod => mod.GestioneScadenzeModule),
            },
            {
                path: 'scadenziario-fiscale',
                loadChildren: () => import('./main/scadenziario-fiscale/scadenziario-fiscale.module').then(mod => mod.ScadenziarioFiscaleModule),
            },
            {
                path: 'gestione-affiliazioni',
                loadChildren: () => import('./main/gestione-affiliazioni/gestione-affiliazioni.module').then(m => m.GestioneAffiliazioniModule),
            },
            {
                path: 'gestione-invii-intrastat',
                loadChildren: () => import('./main/gestione-invii-intrastat/gestione-invii-intrastat.module').then(mod => mod.GestioneInviiIntrastatModule),
            },
            {
                path: 'gestione-invii-f24',
                loadChildren: () => import('./main/gestione-invii-f24/gestione-invii-f24.module').then(mod => mod.GestioneInviiF24Module),
            },
            {
                path: 'gestione-bolli',
                loadChildren: () => import('./main/gestione-bolli/gestione-bolli.module').then(m => m.GestioneBolliModule),
            },
            { path: 'fnc', loadChildren: () => import('./main/fnc/fnc.module').then(m => m.FncModule) },
            {
                path: 'fatturazione-elettronica',
                loadChildren: () => import('./main/fatturazione-elettronica/fatturazione-elettronica.module').then(m => m.FatturazioneElettronicaModule),
            },
        ],
    },
    {
        path: 'concordato-preventivo',
        loadChildren: () => import('./main/concordato-preventivo/concordato-preventivo.module').then(m => m.ConcordatoPreventivoModule),
    },
    {
        path: 'concordato-preventivo',
        loadChildren: () => import('./main/concordato-preventivo/concordato-preventivo.module').then(m => m.ConcordatoPreventivoModule),
    },
    {
        path: 'gestione-ateco',
        loadChildren: () => import('./main/gestione-ateco/gestione-ateco.module').then(m => m.GestioneAtecoModule),
    },
    {
        path: '**',
        redirectTo: 'dashboard',
        pathMatch: 'full',
    },
];

@NgModule({
    declarations: [AppComponent],
    exports: [RouterModule],
    imports: [
        RouterModule.forRoot(routes, { useHash: false, enableTracing: false, onSameUrlNavigation: 'reload' }),
        TranslateModule.forRoot(),
        BrowserAnimationsModule,
        BrowserTransferStateModule,
        HttpClientModule,

        // Material moment date module
        MatMomentDateModule,

        // Material
        MatButtonModule,
        MatIconModule,

        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        FuseProgressBarModule,
        FuseSharedModule,
        FuseSidebarModule,
        FuseThemeOptionsModule,

        // App modules
        SharedModule,
        LayoutModule,
        GlobalLoaderModule,
        ConfermaDialogModule,
        DirectivesModule,
    ],
    bootstrap: [AppComponent],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CacheInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JsonParserHttpInterceptor,
            multi: true,
        },
        { provide: JsonParser, useClass: JsonDateParser },
        { provide: LOCALE_ID, useValue: 'it-IT' },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: false } },
        { provide: MAT_DATE_LOCALE, useValue: 'it-IT' },
    ],
})
export class AppModule {
    constructor(injector: Injector) {
        ServiceLocator.injector = injector;
    }
}

registerLocaleData(localeIt);
